/**
 * BL-53 — density-aware glow damping + threat-tier focal hierarchy.
 *
 * The problem (owner: *"ab Welle 15 alles eins"*): every enemy draws a soft
 * outer halo (`EnemyRenderer`, `glowCache.draw(…, rr * 1.6, …)`) on top of a
 * bloom-clearing emissive core. At 100+ entities those additive halos merge
 * into a uniform colour haze — the S80 silhouettes and the boss vanish into a
 * wash. BL-52 fixed the *hues*; this feeds the bloom **less** at density
 * instead of retuning the global bloom (`WebGL2Effects` / `EmissiveMaterial`
 * untouched — the wash is fixed at the glow layer, render-side).
 *
 * Pure + render-only: nothing here is read by the sim. The live enemy count is
 * a render-time read of the array; `livesDamage`/`radius` are read-only tier
 * signals. Determinism-cold → `sim-fullrun-baseline` is unaffected.
 *
 * Seam (BL-44): {@link densityFactor} is a clean, reusable "under load" signal
 * the degrade ladder can share. Focal *pull* (vignette) is BL-30 — this pass
 * only damps the field; it never brightens the apex above its sparse look.
 */

/** Damping engages only above this active count — below it the frame is
 *  byte-identical to S84 (the sparse look stays premium). */
export const DENSITY_SOFT = 40;
/** Full damping is reached at/above this active count. */
export const DENSITY_HARD = 120;
/** The most a *trash* halo can be damped at full density (boss/elite resist
 *  this via {@link focalWeight}). 0.8 → trash halo keeps ~20% of its glow. */
export const HALO_DAMP_MAX = 0.8;

/**
 * 0 below {@link DENSITY_SOFT} (no damping at all), ramping smoothly to 1 at/
 * above {@link DENSITY_HARD}. Smoothstep (not linear) so there is no hard
 * cliff as a wave crosses the threshold — the haze recedes gracefully.
 */
export function densityFactor(activeCount: number): number {
  if (activeCount <= DENSITY_SOFT) return 0;
  if (activeCount >= DENSITY_HARD) return 1;
  const t = (activeCount - DENSITY_SOFT) / (DENSITY_HARD - DENSITY_SOFT);
  return t * t * (3 - 2 * t); // smoothstep
}

/**
 * BL-44 Part A — density-aware kill hit-stop tier. As the field crowds, a kill's
 * §6.8 freeze steps down so a kill-storm can't saturate the loop ("hängt total"):
 * sparse keeps the full `short` juice, mid-density drops to a `micro` tap, and a
 * packed board takes no freeze at all (let the storm run). Keyed to the SAME
 * {@link densityFactor} the glow damping uses — ONE density model, not a new set
 * of magic numbers. Pure + loop/render-side: never read by the sim (the witness
 * bypasses the GameLoop), so it cannot move the golden master.
 *
 *   ≤ DENSITY_SOFT (40)  → "short"  · full kill tier (byte-identical sparse feel)
 *   in the band          → "micro"  · a 1-frame tap
 *   ≥ DENSITY_HARD (120) → "none"   · no freeze
 *
 * Returns plain string literals (not the engine `HitStopTier` type) so this stays
 * dependency-free; the caller maps "short"/"micro" → `hitStopTier`, "none" → skip.
 */
export function killHitStopTier(activeCount: number): "short" | "micro" | "none" {
  const d = densityFactor(activeCount);
  if (d >= 1) return "none";
  if (d > 0) return "micro";
  return "short";
}

/** The render-side tier signal — both fields are read-only sim outputs. */
export interface GlowTierSignal {
  livesDamage: number;
  radius: number;
}

/**
 * How strongly an enemy **resists** density damping (1 = never damped).
 * Derived from the explicit threat signal `livesDamage`, with body `radius`
 * as a tie-breaker so heavy trash (tank / ram) keeps a touch more presence
 * than a swarm cell:
 *
 *   final_boss (ld≥5) → 1.00  · the AXIOM-77 Core apex, untouched at any density
 *   boss       (ld≥3) → 0.92  · the Core Breaker — stays dominant
 *   elite      (ld≥2) → 0.60  · Corrupted Node / Cipher Breaker
 *   heavy trash (r≥13)→ 0.34  · Rootkit / Firewall Breaker tanks
 *   trash             → 0.18  · Data Packet / Nano-Swarm / Worm
 */
export function focalWeight(e: GlowTierSignal): number {
  const ld = e.livesDamage;
  if (ld >= 5) return 1.0;
  if (ld >= 3) return 0.92;
  if (ld >= 2) return 0.6;
  return e.radius >= 13 ? 0.34 : 0.18;
}

/**
 * The halo alpha/radius multiplier for an enemy at the current density.
 * Returns exactly `1` when sparse (or for the apex), so the caller's sparse
 * path is byte-identical to S84; under crowding it drops toward
 * `1 - HALO_DAMP_MAX` for trash while boss/elite stay near full — the apex
 * dominates and the trash field recedes.
 */
export function haloGlowScale(e: GlowTierSignal, activeCount: number): number {
  const d = densityFactor(activeCount);
  if (d === 0) return 1;
  return 1 - d * (1 - focalWeight(e)) * HALO_DAMP_MAX;
}
